<template>
  <div ref="scrollArea" class="compact-log-scroll" tabindex="0" aria-label="紧凑日志表格">
    <table class="compact-log-table" :class="{ 'is-empty': !rows.length }">
      <colgroup><col v-for="group in groups" :key="group.key" :style="{ width: columnWidth(group) }" /></colgroup>
      <thead><tr><th v-for="group in groups" :key="group.key" scope="col">{{ group.label }}</th></tr></thead>
      <tbody>
        <template v-for="(row, index) in rows" :key="index">
          <tr class="log-record" :class="{ 'is-expanded': expandedRow === row, 'is-stripe': index % 2 }">
            <td v-for="group in groups" :key="group.key">
              <div v-if="group.key === 'time'" class="log-meta-lines">
                <div class="log-time" :title="visible('CreatedAt') ? logFieldText(row.CreatedAt) : undefined">
                  <template v-if="visible('CreatedAt')">
                    <span v-for="(part, partIndex) in splitLogTimestamp(row.CreatedAt)" :key="partIndex" :class="{ muted: partIndex === 0 }">{{ part }}</span>
                  </template>
                </div>
                <div><span v-if="visible('FuncType')">功能 {{ logFieldText(row.FuncType) }}</span></div>
                <div><span v-if="visible('runtime')">{{ logFieldText(row.runtime) }}</span></div>
              </div>
              <div v-else-if="group.key === 'vehicle'" class="log-meta-lines">
                <div class="log-identifier"><span v-if="visible('Vin')">{{ logFieldText(row.Vin) }}</span></div>
                <div class="log-identifier"><template v-if="visible('Sn')"><span class="muted">SN </span>{{ logFieldText(row.Sn) }}</template></div>
                <div><span v-if="visible('SnType')" class="log-user-type">{{ logFieldText(row.SnType) }}</span></div>
                <div>
                  <span v-if="paramPrefix(row)" class="log-protocol" :class="paramPrefix(row)">{{ paramPrefix(row).toUpperCase() }}</span>
                </div>
              </div>
              <div v-else-if="group.key === 'software'" class="log-meta-lines">
                <div><span v-if="visible('DiagId')">{{ logFieldText(row.DiagId) }}</span></div>
                <div><span v-if="visible('Version')">{{ logFieldText(row.Version) }}</span></div>
                <div><span v-if="visible('ProductType')">{{ logFieldText(row.ProductType) }}</span></div>
                <div><span v-if="visible('Language')">{{ logFieldText(row.Language) }}</span></div>
              </div>
              <template v-else-if="group.key === 'request' || group.key === 'response'">
                <div class="log-preview">{{ previewText(row, group) }}</div>
                <button class="log-text-button log-detail-trigger" :aria-expanded="expandedRow === row" :aria-label="`${expandedRow === row ? '收起' : '查看'}第 ${index + 1} 条${group.label}详情`" @click="toggleDetails(row)">{{ expandedRow === row ? '收起' : '详情' }}</button>
              </template>
              <template v-else-if="group.key === 'network'">
                <div v-if="visible('Ip')" class="log-network-field"><span class="muted">IP</span><span>{{ logFieldText(row.Ip) }}</span></div>
                <div v-if="visible('WifiName')" class="log-network-field"><span class="muted">WiFi</span><span>{{ logFieldText(row.WifiName) }}</span></div>
                <div v-if="visible('LocalMac')" class="log-network-field"><span class="muted">LocalMac</span><span class="log-mac">{{ logFieldText(row.LocalMac) }}</span></div>
                <div v-if="visible('WifiMac')" class="log-network-field"><span class="muted">WifiMac</span><span class="log-mac">{{ logFieldText(row.WifiMac) }}</span></div>
              </template>
              <button v-if="!payloadColumns.length && group === groups[groups.length - 1]" class="log-text-button log-detail-trigger" :aria-expanded="expandedRow === row" @click="toggleDetails(row)">{{ expandedRow === row ? '收起' : '详情' }}</button>
            </td>
          </tr>
          <tr v-if="expandedRow === row" class="log-expanded-row">
            <td :colspan="groups.length">
              <section class="log-details" aria-label="日志详情">
                <div class="log-detail-toolbar">
                  <span class="muted">日志详情</span>
                  <div class="log-detail-modes" aria-label="详情格式">
                    <button class="log-text-button" :class="{ active: detailMode === 'formatted' }" :aria-pressed="detailMode === 'formatted'" @click="detailMode = 'formatted'">格式化</button>
                    <span class="muted">/</span>
                    <button class="log-text-button" :class="{ active: detailMode === 'raw' }" :aria-pressed="detailMode === 'raw'" @click="detailMode = 'raw'">原文</button>
                  </div>
                </div>
                <div class="log-code-grid">
                  <section v-for="column in payloadColumns" :key="column.prop" class="log-code-pane">
                    <div class="log-code-header">
                      <strong>{{ column.label }}</strong>
                      <button class="log-copy-button" :aria-label="`复制${column.label}原文`" @click="copyRaw(row[column.prop], column.prop)"><el-icon><CopyDocument /></el-icon>{{ copiedProp === column.prop ? '已复制' : '复制原文' }}</button>
                    </div>
                    <pre v-if="detailMode === 'raw'" tabindex="0"><code>{{ rawText(row[column.prop]) }}</code></pre>
                    <!-- highlight.js 对日志文本转义后生成受控高亮标记。 -->
                    <pre v-else class="log-numbered-code" tabindex="0"><code v-html="highlightDetail(row[column.prop], column.prop)"></code></pre>
                  </section>
                </div>
                <dl v-if="extraColumns.length || !payloadColumns.length" class="log-extra-fields">
                  <div v-for="column in payloadColumns.length ? extraColumns : columns" :key="column.prop"><dt>{{ column.label }}</dt><dd>{{ logFieldText(row[column.prop]) }}</dd></div>
                </dl>
              </section>
            </td>
          </tr>
        </template>
        <tr v-if="!rows.length"><td :colspan="Math.max(groups.length, 1)" class="log-empty">暂无日志数据</td></tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { CopyDocument } from '@element-plus/icons-vue';
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';
import { splitLogParamPrefix } from '../utils/logTextFormat.mjs';
import { COMPACT_LOG_GROUPS, formatLogDetail, getCompactLogGroups, logFieldText, splitLogTimestamp } from '../utils/logCompactPresentation.mjs';

hljs.registerLanguage('json', json);
const props = defineProps({ rows: { type: Array, required: true }, columns: { type: Array, required: true } });
const scrollArea = ref(null);
const expandedRow = ref(null);
const detailMode = ref('formatted');
const copiedProp = ref('');
const groups = computed(() => {
  const result = getCompactLogGroups(props.columns);
  return result.length ? result : [{ key: 'extra', label: '日志信息', props: [] }];
});
const visibleProps = computed(() => new Set(props.columns.map(column => column.prop)));
const visible = prop => visibleProps.value.has(prop);
const payloadColumns = computed(() => props.columns.filter(column => ['DiagParam', 'ResData'].includes(column.prop)));
const summaryProps = new Set(COMPACT_LOG_GROUPS.flatMap(group => group.props));
const extraColumns = computed(() => props.columns.filter(column => !summaryProps.has(column.prop)));
const rawText = value => value === null || value === undefined ? '' : String(value);
const paramPrefix = row => visible('DiagParam') ? splitLogParamPrefix(row.DiagParam, 'DiagParam').prefix : '';
const previewText = (row, group) => logFieldText(splitLogParamPrefix(row[group.props[0]], group.props[0]).content);
const highlightDetail = (value, prop) => formatLogDetail(value, prop).split('\n').map(line =>
  `<span class="log-code-line">${hljs.highlight(line, { language: 'json', ignoreIllegals: true }).value}\n</span>`
).join('');
const columnWidth = group => {
  const total = groups.value.reduce((sum, item) => sum + (item.share || 0), 0);
  return total ? `${group.share / total * 100}%` : 'auto';
};
function toggleDetails(row) {
  expandedRow.value = expandedRow.value === row ? null : row;
  copiedProp.value = '';
}
async function copyRaw(value, prop) {
  try {
    await navigator.clipboard.writeText(rawText(value));
    copiedProp.value = prop;
  } catch {
    ElMessage.error('复制失败，请选中原文手动复制');
  }
}
// 换页、换服务器或重新查询后，不把展开状态带到其他记录。
watch(() => props.rows, () => {
  expandedRow.value = null;
  copiedProp.value = '';
  if (scrollArea.value) scrollArea.value.scrollTop = 0;
});
</script>

<style scoped>
.compact-log-scroll {
  --log-bg: #fff;
  --log-stripe: #f8fafc;
  --log-head: #f1f5f9;
  --log-border: #e2e8f0;
  --log-text: #334155;
  --log-muted: #64748b;
  --log-active: #eff6ff;
  --log-code: #f8fafc;
  --log-string: #397423;
  --log-number: #ad4056;
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--log-border);
  border-radius: 3px;
  background: var(--log-bg);
  color: var(--log-text);
}
:global(html.dark .compact-log-scroll) {
  --log-bg: #0e1929;
  --log-stripe: #111e30;
  --log-head: #152338;
  --log-border: #27354a;
  --log-text: #d7e0ec;
  --log-muted: #98a7bb;
  --log-active: #13283f;
  --log-code: #0c1726;
  --log-string: #a3cf82;
  --log-number: #d68eab;
}
.compact-log-table { width: 100%; min-width: 1040px; table-layout: fixed; border-collapse: collapse; font-family: inherit; font-size: var(--el-font-size-base, 14px); font-weight: 400; line-height: 23px; }
.compact-log-table.is-empty { height: 100%; }
.compact-log-table.is-empty thead { height: 46px; }
th { position: sticky; top: 0; z-index: 1; padding: 11px 12px; text-align: left; background: var(--log-head); color: var(--log-text); font-weight: 600; box-shadow: inset 0 -1px var(--log-border); }
th + th { border-left: 1px solid var(--log-border); }
.log-record td { padding: 8px 12px; vertical-align: top; border-bottom: 1px solid var(--log-border); overflow-wrap: anywhere; }
.log-record td + td { border-left: 1px solid var(--log-border); }
.log-record.is-stripe { background: var(--log-stripe); }
.log-record:hover, .log-record.is-expanded { background: var(--log-active); }
.log-record.is-expanded td:first-child { box-shadow: inset 3px 0 #409eff; }
.muted { color: var(--log-muted); }
.log-meta-lines { display: grid; grid-template-rows: repeat(4, minmax(23px, auto)); }
.log-time { grid-row: span 2; }
.log-time span { display: block; white-space: nowrap; font-variant-numeric: tabular-nums; }
.log-identifier { white-space: nowrap; }
.log-user-type { display: inline-block; padding: 0 6px; border-radius: 3px; background: var(--log-border); color: var(--log-text); font-size: 12px; line-height: 20px; }
.log-network-field { display: grid; grid-template-columns: 64px minmax(0, 1fr); column-gap: 6px; }
.log-mac { white-space: nowrap; }
.log-preview { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere; white-space: pre-wrap; font: inherit; }
.log-protocol { display: inline-block; margin-right: 4px; padding: 0 4px; border: 1px solid currentColor; border-radius: 2px; color: #409eff; font: 600 10px/15px 'Segoe UI', sans-serif; }
.log-protocol.sm2 { color: #67a846; }
button { cursor: pointer; font: inherit; }
button:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
.log-text-button { padding: 0; border: 0; background: transparent; color: #409eff; font-size: 12px; line-height: 20px; }
.log-text-button:hover { text-decoration: underline; }
.log-detail-trigger { display: block; margin-left: auto; margin-top: 2px; }
.log-expanded-row > td { padding: 0; border-bottom: 1px solid var(--log-border); }
.log-details { padding: 8px 12px 12px; box-shadow: inset 3px 0 #409eff; background: var(--log-active); }
.log-detail-toolbar, .log-code-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.log-detail-toolbar { margin-bottom: 8px; font-size: 12px; }
.log-detail-modes { display: flex; align-items: center; gap: 12px; }
.log-detail-modes button:not(.active) { color: var(--log-muted); }
.log-detail-modes .active { box-shadow: 0 2px #409eff; }
.log-code-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); gap: 12px; }
.log-code-pane { min-width: 0; padding: 10px; border: 1px solid var(--log-border); border-radius: 3px; background: var(--log-code); }
.log-code-header { justify-content: flex-start; margin-bottom: 8px; }
.log-code-header strong { font-size: 13px; font-weight: 500; }
.log-copy-button { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: transparent; color: var(--log-muted); font-size: 12px; }
.log-copy-button:hover { color: #409eff; }
pre { margin: 0; padding: 10px; min-height: 128px; max-height: 340px; overflow: auto; border: 1px solid var(--log-border); border-radius: 3px; white-space: pre; tab-size: 2; }
code { font: 14px/23px Consolas, 'Microsoft YaHei', monospace; }
.log-numbered-code { counter-reset: log-line; }
:deep(.log-code-line) { display: block; min-height: 23px; }
:deep(.log-code-line::before) { counter-increment: log-line; content: counter(log-line); display: inline-block; width: 24px; margin-right: 14px; padding-right: 8px; border-right: 1px solid var(--log-border); text-align: right; color: var(--log-muted); user-select: none; }
:deep(.hljs-attr), :deep(.hljs-string) { color: var(--log-string); }
:deep(.hljs-number), :deep(.hljs-literal) { color: var(--log-number); }
.log-extra-fields { display: flex; flex-wrap: wrap; gap: 8px 24px; margin: 10px 0 0; font-size: 12px; }
.log-extra-fields > div { display: flex; gap: 8px; min-width: 0; }
dt { color: var(--log-muted); }
dd { margin: 0; overflow-wrap: anywhere; }
.log-empty { padding: 44px; text-align: center; color: var(--log-muted); }
</style>
